// A PIECE OF THE PAGE, SAVED AS A PICTURE — on white, whatever the screen is set to.
//
// The assembly tree is drawn in HTML: boxes, borders and one-pixel rules. There is no image
// anywhere in it to right-click, and it is the one thing on the page somebody wants to put in
// a document. So one is made here — the element is cloned, every style that decides how it
// looks is written onto the copy as an inline rule, and the copy is handed back to the browser
// inside an SVG <foreignObject>, which is the only way to ask it to paint HTML into a bitmap.
// A canvas turns that into a PNG.
//
// WHY EVERY STYLE HAS TO TRAVEL WITH IT. An SVG loaded as an image is its own small document
// with no access to the page it came from: no stylesheet, no classes, no network, no fonts but
// the ones the machine already has. Anything the drawing needs in order to look like itself
// has to be inside it. That is why each element carries its own MEASUREMENTS as well as its own
// paint: a renderer free to lay the thing out again would re-wrap every label in whatever font
// it did have, and a tree whose chips have moved is a tree whose rules point at nothing.
//
// AND WHY THE PICTURE IS ALWAYS THE LIGHT ONE. Half the time this page is read on a dark
// background, where the drawing is pale text on near-black — saved as it stands that is a
// picture that can only be looked at on a screen, and it is going into a document. So the dark
// class comes off the page for exactly as long as it takes to read the colours back, in one
// stretch of synchronous work with no paint in the middle of it, and goes straight back on.
// Nothing flashes, because nothing is drawn while it is off.

// WHAT DECIDES HOW AN ELEMENT LOOKS. Not everything computed — a full computed style is some
// three hundred and fifty declarations, and on a tree of three hundred elements that is a
// megabyte of text per hundred nodes, most of it defaults. This is the list that matters for a
// drawing made of filled boxes, borders and text.
const PAINTED = [
  'display', 'flex-direction', 'align-items', 'justify-content', 'flex-shrink', 'flex-grow',
  'position', 'top', 'right', 'bottom', 'left',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'background-color', 'color', 'opacity',
  'border-style', 'border-color', 'border-radius',
  'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
  'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant-numeric',
  'line-height', 'letter-spacing', 'text-align', 'white-space', 'overflow-wrap',
];

// The white border round the drawing, so nothing sits against the edge of the file.
const MARGIN = 24;

// HOW BIG THE FILE MAY GET. Twice the size on screen is what makes text sharp when the picture
// is scaled back down in a document; a whole manual's tree is large enough that twice its size
// is a bitmap no browser will allocate, so the factor gives way before the picture does.
const MAX_PIXELS = 30e6;

const styleOnto = (from: Element, to: HTMLElement) => {
  const computed = getComputedStyle(from);
  for (const prop of PAINTED) to.style.setProperty(prop, computed.getPropertyValue(prop));
  // THE SIZE IT ACTUALLY CAME OUT, frozen. Content-box, because that is what `width` reports,
  // and saying so is what keeps the padding copied just above from being counted twice.
  to.style.setProperty('box-sizing', 'content-box');
  to.style.setProperty('width', computed.width);
  to.style.setProperty('height', computed.height);
};

// The element as it stands, on white, as a PNG the browser saves. `name` is the file's, and is
// cut down to something a filesystem is happy with.
export async function downloadNodePng(node: HTMLElement, name: string): Promise<void> {
  const clone = node.cloneNode(true) as HTMLElement;

  // The two trees are the same shape — one is a deep copy of the other — so walking them
  // side by side pairs every element with its own.
  styleOnto(node, clone);
  const from = node.querySelectorAll<HTMLElement>('*');
  const to = clone.querySelectorAll<HTMLElement>('*');
  for (let i = 0; i < from.length; i += 1) styleOnto(from[i], to[i]);

  // The copy stands on its own: whatever placed it on the page — a scale to fit the card it
  // was in, a margin from a neighbour — is not part of the drawing.
  clone.style.setProperty('transform', 'none');
  clone.style.setProperty('position', 'static');
  for (const side of ['top', 'right', 'bottom', 'left']) {
    clone.style.setProperty(`margin-${side}`, '0px');
  }

  const w = node.offsetWidth;
  const h = node.offsetHeight;
  const page = document.createElement('div');
  page.style.cssText = `display:block;box-sizing:content-box;background:#ffffff;padding:${MARGIN}px;width:${w}px;height:${h}px;`;
  page.appendChild(clone);

  const width = w + MARGIN * 2;
  const height = h + MARGIN * 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`
    + `<foreignObject x="0" y="0" width="100%" height="100%">`
    + new XMLSerializer().serializeToString(page)
    + `</foreignObject></svg>`;

  const image = new Image();
  await new Promise<void>((done, fail) => {
    image.onload = () => done();
    image.onerror = () => fail(new Error('tree image'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

  const factor = Math.max(1, Math.min(2, Math.sqrt(MAX_PIXELS / (width * height))));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * factor);
  canvas.height = Math.round(height * factor);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // White under the drawing as well as around it: the chips are drawn on a page and the page
  // is what the SVG has just painted, but a PNG with any transparency left in it comes out
  // black in half the readers it will be opened in.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) return;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'tree'}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
